// /**
//  * useInvoiceList.ts
//  *
//  * Custom hook that owns ALL state and logic for the ViewInvoice page.
//  * The component becomes pure UI — no business logic mixed in.
//  *
//  * Separation of concerns:
//  *  - Data fetching + caching
//  *  - Client-side filtering (search, status, date range, invoice type)
//  *  - Pagination (client-side on the filtered set)
//  *  - Delete handler with Swal confirmation
//  */

// import { useState, useEffect, useMemo, useCallback } from "react";
// import { useNavigate } from "react-router-dom";
// import Swal from "sweetalert2";
// import { parseISO, format, isWithinInterval } from "date-fns";
// import { getInvoices, deleteInvoice } from "../../Services/InvoicePageService";
// import { IInvoiceData } from "../../Interfaces/IInvoice";

// // ─── Filter state shape ───────────────────────────────────────────────────────

// export interface InvoiceFilters {
//   search:       string;   // invoice ID, customer name, order ID
//   status:       string;   // "all" | "due" | "paid" | ""
//   invoiceType:  string;   // "all" | "Original" | "GSTInvoice" | "QuickInvoice" | "PerformaInvoice"
//   fromDate:     string;   // ISO date string "YYYY-MM-DD"
//   toDate:       string;
// }

// const DEFAULT_FILTERS: InvoiceFilters = {
//   search:      "",
//   status:      "all",
//   invoiceType: "all",
//   fromDate:    "",
//   toDate:      "",
// };

// // ─── Hook ─────────────────────────────────────────────────────────────────────

// export function useInvoiceList() {
//   const navigate = useNavigate();
//   const orgId    = localStorage.getItem("orgId") || "";

//   // Raw data from server
//   const [invoices,  setInvoices]  = useState<IInvoiceData[]>([]);
//   const [loading,   setLoading]   = useState(true);
//   const [fetchError,setFetchError]= useState("");

//   // Filters
//   const [filters,   setFilters]   = useState<InvoiceFilters>(DEFAULT_FILTERS);

//   // Pagination (client-side over filtered results)
//   const [page,      setPage]      = useState(0);
//   const [pageSize,  setPageSize]  = useState(10);

//   // ── Fetch ───────────────────────────────────────────────────────────────
//   const fetchInvoices = useCallback(async () => {
//     if (!orgId) return;
//     setLoading(true);
//     setFetchError("");
//     try {
//       const data: IInvoiceData[] = await getInvoices(orgId);
//       // Ensure every row has a stable `id` field for DataGrid
//       const withId = (data || []).map((inv) => ({
//         ...inv,
//         id: inv.invoiceId,
//       }));
//       setInvoices(withId);
//     } catch (err: any) {
//       setFetchError(err?.message || "Failed to load invoices.");
//     } finally {
//       setLoading(false);
//     }
//   }, [orgId]);

//   useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

//   // ── Client-side filtering ───────────────────────────────────────────────
//   const filtered = useMemo<IInvoiceData[]>(() => {
//     const q = filters.search.toLowerCase().trim();

//     return invoices.filter((inv) => {
//       // 1. Text search
//       if (q) {
//         const matchText =
//           inv.invoiceId?.toLowerCase().includes(q)       ||
//           inv.customerName?.toLowerCase().includes(q)    ||
//           inv.orderId?.toLowerCase().includes(q)         ||
//           inv.salesPersonName?.toLowerCase().includes(q) ||
//           inv.invoiceType?.toLowerCase().includes(q);
//         if (!matchText) return false;
//       }

//       // 2. Status filter
//       if (filters.status !== "all" && inv.status !== filters.status) return false;

//       // 3. Invoice type filter
//       if (filters.invoiceType !== "all" && inv.invoiceType !== filters.invoiceType) return false;

//       // 4. Date range filter (on invoiceDate)
//       if (filters.fromDate || filters.toDate) {
//         try {
//           const invDate = parseISO(inv.invoiceDate ?? "");
//           if (filters.fromDate) {
//             const from = parseISO(filters.fromDate);
//             if (invDate < from) return false;
//           }
//           if (filters.toDate) {
//             const to = parseISO(filters.toDate);
//             to.setHours(23, 59, 59, 999);
//             if (invDate > to) return false;
//           }
//         } catch { /* skip unparseable dates */ }
//       }

//       return true;
//     });
//   }, [invoices, filters]);

//   // ── Summary stats (over filtered set) ──────────────────────────────────
//   const stats = useMemo(() => {
//     const totalGrand  = filtered.reduce((s, i) => s + Number(i.grandTotal      || 0), 0);
//     const totalPaid   = filtered.reduce((s, i) => s + Number(i.totalPaidAmount || 0), 0);
//     const totalDue    = filtered.reduce((s, i) => s + Number(i.dueAmount       || 0), 0);
//     const countDue    = filtered.filter((i) => i.status === "due").length;
//     const countPaid   = filtered.filter((i) => i.status === "paid").length;
//     return { totalGrand, totalPaid, totalDue, countDue, countPaid, total: filtered.length };
//   }, [filtered]);

//   // ── Paginated slice ─────────────────────────────────────────────────────
//   const paginated = useMemo(
//     () => filtered.slice(page * pageSize, page * pageSize + pageSize),
//     [filtered, page, pageSize]
//   );

//   // ── Handlers ────────────────────────────────────────────────────────────
//   const updateFilter = useCallback(
//     <K extends keyof InvoiceFilters>(key: K, value: InvoiceFilters[K]) => {
//       setFilters((prev) => ({ ...prev, [key]: value }));
//       setPage(0); // reset to page 1 on any filter change
//     },
//     []
//   );

//   const clearFilters = useCallback(() => {
//     setFilters(DEFAULT_FILTERS);
//     setPage(0);
//   }, []);

//   const hasActiveFilters = useMemo(
//     () =>
//       filters.search      !== DEFAULT_FILTERS.search      ||
//       filters.status      !== DEFAULT_FILTERS.status      ||
//       filters.invoiceType !== DEFAULT_FILTERS.invoiceType ||
//       filters.fromDate    !== DEFAULT_FILTERS.fromDate    ||
//       filters.toDate      !== DEFAULT_FILTERS.toDate,
//     [filters]
//   );

//   const handleView = useCallback((invoice: IInvoiceData) => {
//     localStorage.setItem("customerId", invoice.customerId ?? "");
//     window.open(
//       `/dashboard/sales/view-invoice-details?invoiceId=${invoice.invoiceId}`,
//       "_blank"
//     );
//   }, []);

//   const handleEdit = useCallback(
//     (invoice: IInvoiceData) => {
//       navigate("/dashboard/sales/edit-invoice", { state: { invoice } });
//     },
//     [navigate]
//   );

//   const handleDelete = useCallback(
//     async (invoiceId: string, invoiceType: string) => {
//       const result = await Swal.fire({
//         title:             "Delete Invoice?",
//         text:              "This will cancel the invoice and restore inventory stock. This cannot be undone.",
//         icon:              "warning",
//         showCancelButton:  true,
//         confirmButtonColor:"#d33",
//         cancelButtonColor: "#6c757d",
//         confirmButtonText: "Yes, delete it",
//         cancelButtonText:  "Cancel",
//       });

//       if (!result.isConfirmed) return;

//       try {
//         await deleteInvoice(invoiceId, orgId, "sysAdmin", invoiceType);
//         // Remove from local state instantly (no re-fetch needed)
//         setInvoices((prev) => prev.filter((inv) => inv.invoiceId !== invoiceId));
//         Swal.fire({ title: "Deleted!", text: "Invoice has been cancelled.", icon: "success", timer: 1500, showConfirmButton: false });
//       } catch (err: any) {
//         Swal.fire("Error", err?.message || "Failed to delete invoice.", "error");
//       }
//     },
//     [orgId]
//   );

//   const handleRefresh = useCallback(() => fetchInvoices(), [fetchInvoices]);

//   return {
//     // data
//     invoices,
//     filtered,
//     paginated,
//     stats,
//     loading,
//     fetchError,
//     // filters
//     filters,
//     updateFilter,
//     clearFilters,
//     hasActiveFilters,
//     // pagination
//     page,
//     pageSize,
//     setPage,
//     setPageSize,
//     // actions
//     handleView,
//     handleEdit,
//     handleDelete,
//     handleRefresh,
//   };
// }
/**
 * useInvoiceList.ts
 *
 * Server-side pagination + filtering. Only the current page of invoices
 * lives in state; filters/page/pageSize changes trigger a re-fetch.
 * Stats are fetched separately (aggregated over the full filtered set,
 * not just the current page).
 */
/**
 * useInvoiceList.ts
 *
 * Now backed by /invoice/salesRegister — one call gives us the current
 * page, summary totals (over the full filtered set), and total count.
 * No separate stats or export endpoints needed.
 */
/**
 * useInvoiceList.ts
 *
 * Now backed by /invoice/salesRegister — one call gives us the current
 * page, summary totals (over the full filtered set), and total count.
 * No separate stats or export endpoints needed.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { getSalesRegister, deleteInvoice } from "../../Services/InvoicePageService";
import { IInvoiceData } from "../../Interfaces/IInvoice";
import { ISalesRegisterRow } from "../../Interfaces/IInvoice";

export interface InvoiceFilters {
  search:       string;
  status:       string;   // "all" | "due" | "paid" | ""
  invoiceType:  string;   // "all" | "Original" | "GSTInvoice" | "QuickInvoice" | "PerformaInvoice"
  fromDate:     string;
  toDate:       string;
}

export interface InvoiceStats {
  total: number;
  totalGrand: number;
  totalPaid: number;
  totalDue: number;
  countDue: number;
  countPaid: number;
}

const DEFAULT_FILTERS: InvoiceFilters = {
  search: "", status: "all", invoiceType: "all", fromDate: "", toDate: "",
};

const DEFAULT_STATS: InvoiceStats = {
  total: 0, totalGrand: 0, totalPaid: 0, totalDue: 0, countDue: 0, countPaid: 0,
};

const SEARCH_DEBOUNCE_MS = 350;

// Adapts a sales-register row back into the field names ViewInvoice.tsx
// already expects, so the component doesn't need to change.
function mapRow(row: ISalesRegisterRow): IInvoiceData {
  return {
    ...(row as any),
    id:              row.id,
    invoiceId:       row.invoiceId,
    subTotalAmount:  row.taxableAmount,
    totalTax:        Number((row.cgst + row.sgst + row.igst).toFixed(2)),
    totalDiscount:   row.totalDiscount,
    shippingCharge:  row.shippingCharge,
    grandTotal:      row.grandTotal,
    totalPaidAmount: row.totalPaidAmount,
    dueAmount:       row.dueAmount,
    status:          row.status,
    invoiceType:     row.invoiceType,
    customerName:    row.customerName,
    customerId:      row.customerId,
    invoiceDate:     row.invoiceDate,
    dueDate:         row.dueDate,
    salesPersonName: row.salesPersonName,
    orderId:         row.orderId,
    paymentMode:     row.paymentMode,
  } as IInvoiceData;
}

export function useInvoiceList() {
  const navigate = useNavigate();
  const orgId    = localStorage.getItem("orgId") || "";

  const [invoices,   setInvoices]   = useState<IInvoiceData[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [stats,       setStats]     = useState<InvoiceStats>(DEFAULT_STATS);

  const [filters,          setFilters]         = useState<InvoiceFilters>(DEFAULT_FILTERS);
  const [debouncedFilters,  setDebouncedFilters] = useState<InvoiceFilters>(DEFAULT_FILTERS);

  const [page,       setPage]       = useState(0);   // 0-indexed in UI
  const [pageSize,   setPageSize]   = useState(20);  // fast first load
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  // Server-side sort — now real, not just "sorts the visible page"
  const [sortField, setSortField] = useState<string>("invoiceDate");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // ── Debounce search text only ───────────────────────────────────────────
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedFilters(filters);
      setPage(0);
    }, filters.search !== debouncedFilters.search ? SEARCH_DEBOUNCE_MS : 0);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const requestId = useRef(0);

  const fetchInvoices = useCallback(async () => {
    if (!orgId) return;
    const thisRequest = ++requestId.current;
    setLoading(true);
    setFetchError("");
    try {
      const res = await getSalesRegister(orgId, {
        page: page + 1,             // backend is 1-indexed
        limit: pageSize,
        status:      debouncedFilters.status      !== "all" ? debouncedFilters.status      : undefined,
        invoiceType: debouncedFilters.invoiceType  !== "all" ? debouncedFilters.invoiceType  : undefined,
        fromDate:    debouncedFilters.fromDate     || undefined,
        toDate:      debouncedFilters.toDate       || undefined,
        search:      debouncedFilters.search.trim() || undefined,
        sortField,
        sortOrder,
      });

      if (thisRequest !== requestId.current) return; // stale response guard

      setInvoices(res.invoices.map(mapRow));
      setTotalCount(res.pagination.total);
      setTotalPages(Math.max(1, Math.ceil(res.pagination.total / res.pagination.limit)));

      // Summary comes from the SAME call — no separate stats request needed
      setStats({
        total:      res.summary.totalInvoices,
        totalGrand: res.summary.totalSales,
        totalPaid:  res.summary.totalReceived,
        totalDue:   res.summary.totalDue,
        // Backend doesn't currently return per-status counts — see note below
        countDue:   0,
        countPaid:  0,
      });
    } catch (err: any) {
      if (thisRequest !== requestId.current) return;
      setFetchError(err?.message || "Failed to load invoices.");
    } finally {
      if (thisRequest === requestId.current) setLoading(false);
    }
  }, [orgId, page, pageSize, debouncedFilters, sortField, sortOrder]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  // ── Handlers ────────────────────────────────────────────────────────────
  const updateFilter = useCallback(
    <K extends keyof InvoiceFilters>(key: K, value: InvoiceFilters[K]) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const clearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setDebouncedFilters(DEFAULT_FILTERS);
    setPage(0);
  }, []);

  const hasActiveFilters =
    filters.search      !== DEFAULT_FILTERS.search      ||
    filters.status      !== DEFAULT_FILTERS.status      ||
    filters.invoiceType !== DEFAULT_FILTERS.invoiceType ||
    filters.fromDate    !== DEFAULT_FILTERS.fromDate    ||
    filters.toDate      !== DEFAULT_FILTERS.toDate;

  const handleSort = useCallback((field: string) => {
    setSortField((prevField) => {
      if (prevField === field) {
        setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
        return field;
      }
      setSortOrder("desc");
      return field;
    });
    setPage(0);
  }, []);

  const handleView = useCallback((invoice: IInvoiceData) => {
    localStorage.setItem("customerId", invoice.customerId ?? "");
    window.open(`/dashboard/sales/view-invoice-details?invoiceId=${invoice.invoiceId}`, "_blank");
  }, []);

  const handleEdit = useCallback((invoice: IInvoiceData) => {
    // navigate("/dashboard/sales/edit-invoice", { state: { invoice } });
    navigate(`/dashboard/sales/edit-invoice?invoiceId=${invoice.invoiceId}`);
  }, [navigate]);

  const handleDelete = useCallback(
    async (invoiceId: string, invoiceType: string) => {
      const result = await Swal.fire({
        title: "Delete Invoice?",
        text:  "This will cancel the invoice and restore inventory stock. This cannot be undone.",
        icon: "warning", showCancelButton: true,
        confirmButtonColor: "#d33", cancelButtonColor: "#6c757d",
        confirmButtonText: "Yes, delete it", cancelButtonText: "Cancel",
      });
      if (!result.isConfirmed) return;

      try {
        await deleteInvoice(invoiceId, orgId, "sysAdmin", invoiceType);
        await fetchInvoices(); // re-fetch current page + summary together
        Swal.fire({ title: "Deleted!", text: "Invoice has been cancelled.", icon: "success", timer: 1500, showConfirmButton: false });
      } catch (err: any) {
        Swal.fire("Error", err?.message || "Failed to delete invoice.", "error");
      }
    },
    [orgId, fetchInvoices]
  );

  const handleRefresh = useCallback(() => fetchInvoices(), [fetchInvoices]);

  // ── Export: reuse the same endpoint with a high limit ──────────────────
  const exportAll = useCallback(async (): Promise<IInvoiceData[]> => {
    const res = await getSalesRegister(orgId, {
      status:      filters.status      !== "all" ? filters.status      : undefined,
      invoiceType: filters.invoiceType !== "all" ? filters.invoiceType : undefined,
      fromDate:    filters.fromDate    || undefined,
      toDate:      filters.toDate      || undefined,
      search:      filters.search.trim() || undefined,
      sortField, sortOrder,
      page: 1,
      limit: 5000, // hard ceiling — matches backend's own limit clamp (max 100 currently, see note)
    });
    return res.invoices.map(mapRow);
  }, [orgId, filters, sortField, sortOrder]);

  return {
    invoices, stats, loading, fetchError,
    filters, updateFilter, clearFilters, hasActiveFilters,
    page, pageSize, totalPages, totalCount,
    setPage, setPageSize: (n: number) => { setPageSize(n); setPage(0); },
    sortField, sortOrder, handleSort,
    handleView, handleEdit, handleDelete, handleRefresh,
    exportAll,
  };
}